// What a person or an agent put on a slide, drawn the same everywhere a slide shows (the editor, the strip, All boards,
// present and the presenter's window, a shared link, a phone, a printed page): text boxes, shapes with words in them,
// lines and arrows, pictures and live widgets, each at its place in the 1600 by 900 design space, rotated, layered and
// styled (shared/slideDesign.ts), and the slide's own background under everything. The editor adds its handles over
// these in screen space (Design.tsx); nothing here changes a slide.

import { Fragment, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { picturePath } from "../../../shared/imageInfo";
import { colorValue, fontStack, isPlaced, type FieldName, type FieldPlace, type Palette, type ShapeKind, type SlideBg, type SlideBox, type SlideColor, type TextLook } from "../../../shared/slideDesign";
import type { DocNode } from "../../../shared/docblocks";
import type { WidgetItem } from "../../../shared/types";
import { TYPE, type SlideLayout } from "../../../shared/slides";
import { cx } from "../../lib/util";
import { readableOn } from "../../themes/themes";
import { useStore } from "../../state/store";
import { TableCtx, TableGrid } from "./table/TableView";
import { IconSvg } from "./icons/IconView";
import "./fonts";
import { moreAttrs, moreKey, moreStyle } from "./place/words";
import { linePoints, r2 } from "../../../shared/slidePlace";
import "../../styles/slides-place.css";
import { FormatPicture, OutlineSvg, ShapeBody, boxBackground, dashArray, fxFilter, hasFormat, inkOnFill, innerShadow, lineCap, softMask } from "./format/look";
import "../../styles/slides-format.css";
import { ChartBody } from "./chart/ChartBox";
import { DiagramBody } from "./diagram/DiagramBox";
import "../../styles/slides-charts.css";

/** The look a box's words have when it says nothing of its own, by kind. */
export const TEXT_DEFAULT = { size: 34, lineH: 1.3 };
export const SHAPE_DEFAULT = { size: 30, lineH: 1.25 };
/** The size a layout draws a field's words at, when the slide says none of its own (shared/slides.ts TYPE). */
export function fieldSize(layout: SlideLayout, f: FieldName): number {
  if (f === "figure") return TYPE.figure.size;
  if (f === "cite") return TYPE.small.size;
  if (f === "body2") return TYPE.col.size;
  if (f === "title") return ({ title: TYPE.hero.size, section: TYPE.section.size, picture: 48, full: TYPE.full.size, big: TYPE.small.size, quote: TYPE.small.size } as Partial<Record<SlideLayout, number>>)[layout] ?? TYPE.title.size;
  return ({ title: TYPE.sub.size, section: TYPE.col.size, two: TYPE.col.size, big: TYPE.meaning.size, quote: TYPE.quote.size, picture: TYPE.side.size, full: TYPE.side.size } as Partial<Record<SlideLayout, number>>)[layout] ?? TYPE.body.size;
}

/** A line's width, and a border's, when it says none. */
export const LINE_W = 4;

const SHADOW = "drop-shadow(0 10px 18px rgb(0 0 0 / 0.18)) drop-shadow(0 2px 4px rgb(0 0 0 / 0.12))";

/** A colour as drawn on this slide. */
export const paint = (c: SlideColor | null | undefined, palette: Palette, fallback: string) => colorValue(c, palette, fallback);

// ---------------------------------------------------------------------------
// the background

/** The slide's own background, under its layout and boxes: a colour, two colours at an angle, or a picture. */
export function SlideBackground({ bg, palette }: { bg: SlideBg; palette: Palette }) {
  if (bg.kind === "color") return <div className="sl-bg" style={{ background: paint(bg.color, palette, palette.background) }} aria-hidden />;
  if (bg.kind === "gradient") return <div className="sl-bg" style={{ background: `linear-gradient(${bg.angle}deg, ${paint(bg.from, palette, palette.background)}, ${paint(bg.to, palette, palette.background)})` }} aria-hidden />;
  const veil = bg.veil ?? 0;
  return (
    <div className="sl-bg" style={{ background: paint(bg.color, palette, palette.background) }} aria-hidden>
      <img src={picturePath(bg.image.key)} alt="" draggable={false} decoding="async" style={{ objectFit: bg.fit === "fit" ? "contain" : "cover" }} />
      {veil !== 0 && <div className="sl-bg-veil" style={{ background: veil < 0 ? `rgb(0 0 0 / ${-veil})` : `rgb(255 255 255 / ${veil})` }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// shapes

const pts = (p: [number, number][]) => "M" + p.map(([x, y]) => `${r2(x)},${r2(y)}`).join("L") + "Z";

/** A rectangle with round corners, as a path. */
function roundRect(w: number, h: number, r: number): string {
  const k = Math.max(0, Math.min(r, w / 2, h / 2));
  if (!k) return pts([[0, 0], [w, 0], [w, h], [0, h]]);
  return `M${k},0H${w - k}A${k},${k} 0 0 1 ${w},${k}V${h - k}A${k},${k} 0 0 1 ${w - k},${h}H${k}A${k},${k} 0 0 1 0,${h - k}V${k}A${k},${k} 0 0 1 ${k},0Z`;
}

/** The corners a rounded rectangle has when it says none: as Google Slides draws one. */
export const roundOf = (b: Pick<SlideBox, "w" | "h" | "radius">) => b.radius ?? Math.min(b.w, b.h) * 0.16;

/** A shape's outline in its box's own pixels, so its border is as thick all the way round whatever the box's shape. */
export function shapePath(shape: ShapeKind, w: number, h: number, radius?: number): string {
  switch (shape) {
    case "round":
      return roundRect(w, h, radius ?? Math.min(w, h) * 0.16);
    case "ellipse":
      return `M${w / 2},0A${w / 2},${h / 2} 0 1 1 ${w / 2},${h}A${w / 2},${h / 2} 0 1 1 ${w / 2},0Z`;
    case "triangle":
      return pts([[w / 2, 0], [w, h], [0, h]]);
    case "rtriangle":
      return pts([[0, 0], [w, h], [0, h]]);
    case "diamond":
      return pts([[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]]);
    case "pentagon":
      return pts([[w / 2, 0], [w, h * 0.382], [w * 0.809, h], [w * 0.191, h], [0, h * 0.382]]);
    case "hexagon": {
      const d = Math.min(w * 0.25, h * 0.5);
      return pts([[d, 0], [w - d, 0], [w, h / 2], [w - d, h], [d, h], [0, h / 2]]);
    }
    case "arrow": {
      const hx = w - Math.min(h * 0.5, w * 0.5);
      return pts([[0, h * 0.25], [hx, h * 0.25], [hx, 0], [w, h / 2], [hx, h], [hx, h * 0.75], [0, h * 0.75]]);
    }
    case "chevron": {
      const d = Math.min(h * 0.5, w * 0.45);
      return pts([[0, 0], [w - d, 0], [w, h / 2], [w - d, h], [0, h], [d, h / 2]]);
    }
    case "star": {
      const p: [number, number][] = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const r = i % 2 ? 0.382 : 1;
        p.push([w / 2 + Math.cos(a) * r * (w / 2), h * 0.5 + 0.03 * h + Math.sin(a) * r * (h / 2) * 1.05]);
      }
      return pts(p.map(([x, y]) => [x, Math.max(0, Math.min(h, y))]));
    }
    case "bubble": {
      const bh = h * 0.8;
      const k = Math.min(radius ?? Math.min(w, bh) * 0.16, w / 2, bh / 2);
      return `M${k},0H${w - k}A${k},${k} 0 0 1 ${w},${k}V${bh - k}A${k},${k} 0 0 1 ${w - k},${bh}H${w * 0.36}L${w * 0.2},${h}L${w * 0.22},${bh}H${k}A${k},${k} 0 0 1 0,${bh - k}V${k}A${k},${k} 0 0 1 ${k},0Z`;
    }
    case "plus": {
      const t = Math.min(w, h) * 0.36;
      const a = (w - t) / 2;
      const b = (h - t) / 2;
      return pts([[a, 0], [w - a, 0], [w - a, b], [w, b], [w, h - b], [w - a, h - b], [w - a, h], [a, h], [a, h - b], [0, h - b], [0, b], [a, b]]);
    }
    default:
      return pts([[0, 0], [w, 0], [w, h], [0, h]]);
  }
}

/** Where a shape's words sit inside it (top, right, bottom, left, in its pixels): inside what the shape covers. */
export function wordsInset(shape: ShapeKind, w: number, h: number): [number, number, number, number] {
  const p = 24;
  switch (shape) {
    case "ellipse":
      return [h * 0.146, w * 0.146, h * 0.146, w * 0.146];
    case "triangle":
      return [h * 0.45, w * 0.22, h * 0.04, w * 0.22];
    case "rtriangle":
      return [h * 0.45, w * 0.45, h * 0.04, w * 0.06];
    case "diamond":
      return [h * 0.25, w * 0.25, h * 0.25, w * 0.25];
    case "pentagon":
      return [h * 0.3, w * 0.18, h * 0.06, w * 0.18];
    case "hexagon":
      return [p, Math.min(w * 0.25, h * 0.5) * 0.7, p, Math.min(w * 0.25, h * 0.5) * 0.7];
    case "arrow":
      return [h * 0.27, Math.min(h * 0.5, w * 0.5) * 0.6, h * 0.27, p];
    case "chevron": {
      const d = Math.min(h * 0.5, w * 0.45);
      return [p, d, p, d];
    }
    case "star":
      return [h * 0.36, w * 0.3, h * 0.2, w * 0.3];
    case "bubble":
      return [p, p, h * 0.2 + p, p];
    case "plus": {
      const t = Math.min(w, h) * 0.36;
      return [(h - t) / 2, (w - t) / 2, (h - t) / 2, (w - t) / 2];
    }
    default:
      return [p, p, p, p];
  }
}

// every dash a box can have (SB adds PowerPoint's square dots, dash dot and long dashes: format/look.tsx)
const DASH: Record<string, (w: number) => string | undefined> = Object.fromEntries((["solid", "dash", "dot", "square", "dashDot", "long", "longDashDot"] as const).map((k) => [k, (w: number) => dashArray(k, w)]));

// ---------------------------------------------------------------------------
// words

function inlineRuns(nodes: DocNode[] | undefined, palette: Palette): ReactNode[] {
  return (nodes ?? []).map((n, i) => {
    if (n.type === "hardBreak") return <br key={i} />;
    if (n.type !== "text") return <Fragment key={i}>{inlineRuns(n.content, palette)}</Fragment>;
    let el: ReactNode = n.text;
    // a colour wraps the rest (SA), so an underline or a strike takes the words' own colour
    for (const m of [...(n.marks ?? [])].sort((x, y) => Number(x.type === "textStyle") - Number(y.type === "textStyle"))) {
      if (m.type === "bold") el = <strong>{el}</strong>;
      else if (m.type === "italic") el = <em>{el}</em>;
      else if (m.type === "underline") el = <u>{el}</u>;
      else if (m.type === "strike") el = <s>{el}</s>;
      else if (m.type === "textStyle") {
        const st: CSSProperties = {};
        if (typeof m.attrs?.color === "string") st.color = paint(m.attrs.color as SlideColor, palette, "inherit");
        if (typeof m.attrs?.size === "number") st.fontSize = `calc(${m.attrs.size}px * var(--bfit, 1))`;
        el = <span style={st}>{el}</span>;
      } else if (m.type === "link" && typeof m.attrs?.href === "string" && /^(https?:|mailto:)/i.test(m.attrs.href))
        el = (
          <a href={m.attrs.href} target="_blank" rel="noopener noreferrer nofollow" onClick={(e) => e.stopPropagation()}>
            {el}
          </a>
        );
    }
    return <Fragment key={i}>{el}</Fragment>;
  });
}

function wordsBlock(n: DocNode, i: number, palette: Palette): ReactNode {
  switch (n.type) {
    case "paragraph":
      return <p key={i}>{n.content?.length ? inlineRuns(n.content, palette) : <br />}</p>;
    case "bulletList":
      return <ul key={i}>{(n.content ?? []).map((it, j) => <li key={j}>{(it.content ?? []).map((c, k) => wordsBlock(c, k, palette))}</li>)}</ul>;
    case "orderedList":
      return (
        <ol key={i} start={Number(n.attrs?.start) || undefined}>
          {(n.content ?? []).map((it, j) => (
            <li key={j}>{(it.content ?? []).map((c, k) => wordsBlock(c, k, palette))}</li>
          ))}
        </ol>
      );
    default:
      return null;
  }
}

/** A box's words, read only. */
export function BoxWords({ text, palette }: { text: SlideBox["text"]; palette: Palette }) {
  if (!text?.content.length) return null;
  return <>{text.content.map((n, i) => wordsBlock(n, i, palette))}</>;
}

/** A box's words, in two or three columns when it has them (SA). */
function ColWords({ box, palette }: { box: SlideBox; palette: Palette }) {
  if (!box.cols) return <BoxWords text={box.text} palette={palette} />;
  return (
    <div className="sl-bx-cols">
      <BoxWords text={box.text} palette={palette} />
    </div>
  );
}

/** The type a box's words are set in, as styles: the words selected can carry their own colour and size. */
export function wordsStyle(look: TextLook, palette: Palette, theme: { title: string; body: string }, base: { size: number; lineH: number; color: string; font?: "title" | "body"; weight?: number }): CSSProperties {
  const size = look.size ?? base.size;
  return {
    fontFamily: fontStack(look.font ?? base.font ?? "body", theme),
    fontSize: `calc(${size}px * var(--bfit, 1))`,
    lineHeight: look.lineH ?? base.lineH,
    fontWeight: look.weight ?? base.weight ?? (look.font === "title" || base.font === "title" ? "var(--sl-title-weight)" : undefined),
    color: look.color ? paint(look.color, palette, base.color) : base.color,
    textAlign: look.align ?? "left",
    justifyContent: look.valign === "middle" ? "center" : look.valign === "bottom" ? "flex-end" : "flex-start",
    // SA: letter spacing, paragraph spacing and list marks
    ...moreStyle(look),
  };
}

// ---------------------------------------------------------------------------
// fit: a box's words shrink to fit it (unless it grows with them), measured once its fonts are in

/**
 * Shrinks the words in a box until they fit its height, down to 30%, keeping each word whole where that fits (S7: a
 * word broken across lines reads worst of all); told the fit.
 */
export function measureBoxFit(box: HTMLElement): number {
  const words = box.querySelector<HTMLElement>(".sl-bx-words");
  if (!words) return 1;
  const set = (f: number) => box.style.setProperty("--bfit", String(f));
  const over = () => words.scrollHeight > words.clientHeight + 1 || words.scrollWidth > words.clientWidth + 1;
  words.style.overflowWrap = "normal";
  const done = (f: number) => {
    words.style.overflowWrap = "";
    return f;
  };
  set(1);
  if (!over()) return done(1);
  let lo = 0.3;
  let hi = 1;
  set(lo);
  if (over()) return done(lo);
  for (let i = 0; i < 7; i++) {
    const mid = (lo + hi) / 2;
    set(mid);
    if (over()) hi = mid;
    else lo = mid;
  }
  set(lo);
  return done(Math.floor(lo * 100) / 100);
}

function useBoxFit(ref: React.RefObject<HTMLElement | null>, key: string, on: boolean, onFit?: (f: number) => void) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!on) {
      el.style.removeProperty("--bfit");
      return;
    }
    const run = () => {
      const f = measureBoxFit(el);
      onFit?.(f);
    };
    run();
    // a font that loads later measures again
    if (typeof document !== "undefined" && document.fonts && document.fonts.status !== "loaded") {
      let live = true;
      void document.fonts.ready.then(() => live && run());
      return () => {
        live = false;
      };
    }
  }, [key, on]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ---------------------------------------------------------------------------
// a box

export interface BoxCtx {
  palette: Palette;
  /** the deck's title and body fonts, as CSS */
  theme: { title: string; body: string };
  mode: "edit" | "show" | "thumb" | "print";
  renderWidget?: (item: WidgetItem) => ReactNode;
  /** the editor's words for the box being typed in, in place of its read-only words */
  editorFor?: (box: SlideBox) => ReactNode | undefined;
  /** the box's z-index among the slide's layers */
  layer: (z: number) => number;
  /** presenting (S7): a box whose click is to come is hidden, the one just clicked in comes in */
  build?: (id: string) => "hidden" | "fade" | "rise" | undefined;
  /** in the editor with the motion menu open: its place in the slide's click order */
  buildTag?: (id: string) => number | undefined;
}

/** Where a box sits and how it turns, as styles. */
export const boxFrame = (b: { x: number; y: number; w: number; h: number; rot?: number }, grow = false): CSSProperties => ({
  left: b.x,
  top: b.y,
  width: b.w,
  ...(grow ? { minHeight: b.h } : { height: b.h }),
  transform: b.rot ? `rotate(${b.rot}deg)` : undefined,
});

/** One box on a slide, as it is drawn everywhere. */
export function BoxView({ box: b, ctx }: { box: SlideBox; ctx: BoxCtx }) {
  const ref = useRef<HTMLDivElement>(null);
  const editor = ctx.editorFor?.(b);
  const grow = b.fit === "grow" && (b.kind === "text" || b.kind === "shape");
  const hasWords = b.kind === "text" || b.kind === "shape";
  useBoxFit(ref, `${b.w}|${b.h}|${b.size ?? ""}|${b.font ?? ""}|${b.lineH ?? ""}|${b.weight ?? ""}|${JSON.stringify(b.text ?? null)}|${b.shape ?? ""}|${editor ? 1 : 0}|${moreKey(b)}`, hasWords && !grow && !editor && b.fit !== "none");
  // SB: a shadow from the gallery and a glow over the plain soft shadow
  const style: CSSProperties = { ...boxFrame(b, grow), zIndex: ctx.layer(b.z), opacity: b.opacity, filter: fxFilter(b, ctx.palette) ?? (b.shadow ? SHADOW : undefined) };
  const common = { ref, className: cx("sl-bx", `is-${b.kind}`, b.shape && `is-${b.shape}`, editor !== undefined && "is-typing", b.flipH && "is-flip-h", b.flipV && "is-flip-v"), style, "data-box": b.id, "data-build": ctx.build?.(b.id), "data-build-n": ctx.buildTag?.(b.id) } as const;

  if (b.kind === "line") return <LineView box={b} ctx={ctx} style={style} />;

  // SB: a picture cropped, cropped to a shape or adjusted (format/look.tsx)
  if (b.kind === "picture" && b.image && hasFormat(b)) return <FormatPicture b={b} palette={ctx.palette} shape={shapePath} lazy={ctx.mode === "thumb"} outer={common} />;

  if (b.kind === "picture" && b.image) {
    const border = b.line ? `${b.lineW ?? LINE_W}px ${b.dash === "dash" ? "dashed" : b.dash === "dot" ? "dotted" : "solid"} ${paint(b.line, ctx.palette, ctx.palette.text)}` : undefined;
    const fx = (b.fx ?? 0.5) * 100;
    const fy = (b.fy ?? 0.5) * 100;
    const img = (
      <img
        className="sl-bx-img"
        src={picturePath(b.image.key)}
        alt=""
        draggable={false}
        loading={ctx.mode === "thumb" ? "lazy" : "eager"}
        decoding="async"
        style={{ objectFit: b.imgFit === "fit" ? "contain" : "cover", objectPosition: `${fx}% ${fy}%`, transform: b.zoom && b.zoom > 1 ? `scale(${b.zoom})` : undefined, transformOrigin: `${fx}% ${fy}%` }}
      />
    );
    return (
      <div {...common} style={{ ...style, borderRadius: b.radius, border, overflow: "hidden" }}>
        {/* Fixes 8: mirrored as it shows in its box (its zoom and focus kept), from the box's middle */}
        {b.flipH || b.flipV ? <div className="sl-bx-mirror">{img}</div> : img}
      </div>
    );
  }

  if (b.kind === "widget") {
    const item = <WidgetIn id={b.widget} ctx={ctx} />;
    return (
      <div {...common} style={{ ...style, borderRadius: b.radius }}>
        {item}
      </div>
    );
  }

  if (b.kind === "shape") {
    const shape = b.shape ?? "rect";
    const fill = b.fill === null ? "none" : paint(b.fill ?? "accent", ctx.palette, ctx.palette.accent);
    const stroke = b.line ? paint(b.line, ctx.palette, ctx.palette.text) : "none";
    const lw = b.line ? (b.lineW ?? LINE_W) : 0;
    // Fixes 8: a flipped shape's words sit where its mirrored drawing leaves room (an arrow's head on the other side)
    const [t0, r0, b0, l0] = wordsInset(shape, b.w, b.h);
    const [t, btm] = b.flipV ? [b0, t0] : [t0, b0];
    const [r, l] = b.flipH ? [l0, r0] : [r0, l0];
    // words on a shape read on its fill, unless the box says their colour
    const ink = (hasFormat(b) ? inkOnFill(b, ctx.palette) : null) ?? (fill === "none" ? ctx.palette.text : /^#[0-9a-f]{6}$/i.test(fill) ? (readableOn(fill) === "#ffffff" ? "#FFFFFF" : "#111317") : ctx.palette.text);
    return (
      <div {...common}>
        {hasFormat(b) ? (
          // SB: a gradient or a picture fill, an outline's dashes and ends, an inside shadow, soft edges
          <ShapeBody b={b} d={shapePath(shape, b.w, b.h, shape === "round" ? roundOf(b) : b.radius)} fill={fill} stroke={stroke} lw={lw} palette={ctx.palette} />
        ) : (
          <svg className="sl-bx-svg" width={b.w} height={b.h} viewBox={`0 0 ${b.w} ${b.h}`} aria-hidden>
            <path d={shapePath(shape, b.w, b.h, shape === "round" ? roundOf(b) : b.radius)} fill={fill} stroke={stroke} strokeWidth={lw || undefined} strokeDasharray={lw ? DASH[b.dash ?? "solid"](lw) : undefined} strokeLinecap={b.dash === "dot" ? "round" : undefined} strokeLinejoin="round" />
          </svg>
        )}
        {(b.text || editor !== undefined) && (
          <div className="sl-bx-words" {...moreAttrs(b)} style={{ ...wordsStyle(b, ctx.palette, ctx.theme, { ...SHAPE_DEFAULT, color: ink }), top: t, right: r, bottom: btm, left: l, textAlign: b.align ?? "center", justifyContent: b.valign === "top" ? "flex-start" : b.valign === "bottom" ? "flex-end" : "center" }}>
            {editor ?? <ColWords box={b} palette={ctx.palette} />}
          </div>
        )}
      </div>
    );
  }

  // an icon (S7): drawn in the box's colour (the accent unless set), as large as its box
  if (b.kind === "icon" && b.icon) {
    return (
      <div {...common}>
        <IconSvg name={b.icon} color={paint(b.line ?? "accent", ctx.palette, ctx.palette.accent)} stroke={b.lineW ?? 2} className="sl-bx-icon" />
      </div>
    );
  }

  // a table (S7): its grid, growing down when its words need more than its rows' room
  if (b.kind === "table" && b.table) {
    return (
      <div {...common} style={{ ...style, height: undefined, minHeight: b.h }}>
        <TableCtx.Provider value={{ palette: ctx.palette, theme: ctx.theme }}>{editor ?? <TableGrid box={b} palette={ctx.palette} theme={ctx.theme} />}</TableCtx.Provider>
      </div>
    );
  }

  // SD: a chart of the deck's own, and a diagram drawn as the boxes it makes
  if (b.kind === "chart" && b.chart)
    return (
      <div {...common}>
        <ChartBody box={b} ctx={ctx} />
      </div>
    );
  if (b.kind === "diagram" && b.diagram)
    return (
      <div {...common}>
        <DiagramBody box={b} ctx={ctx} />
      </div>
    );

  // a text box: its words, on a fill and in a border when it has them
  const fill = b.fill ? paint(b.fill, ctx.palette, "transparent") : undefined;
  const border = b.line ? `${b.lineW ?? LINE_W}px ${b.dash === "dash" ? "dashed" : b.dash === "dot" ? "dotted" : "solid"} ${paint(b.line, ctx.palette, ctx.palette.text)}` : undefined;
  const ink = b.fill && fill && /^#[0-9a-f]{6}$/i.test(fill) && !b.color ? (readableOn(fill) === "#ffffff" ? "#FFFFFF" : "#111317") : ctx.palette.text;
  // SB: a gradient or a picture behind the words, an outline's dashes and ends drawn as a path, effects inside the box
  if (hasFormat(b)) {
    const bg = boxBackground(b, ctx.palette);
    const lw = b.line ? (b.lineW ?? LINE_W) : 0;
    const d = b.radius ? shapePath("round", b.w, b.h, b.radius) : `M0,0H${b.w}V${b.h}H0Z`;
    const padded = !!(fill || border || bg);
    return (
      <div {...common} style={style}>
        {(fill || bg || innerShadow(b)) && <div className="sl-bx-fill" style={{ background: bg ? undefined : fill, ...(bg ?? {}), borderRadius: b.radius, boxShadow: innerShadow(b), ...softMask(b.soft) }} />}
        <div className={cx("sl-bx-words", padded && "is-padded")} {...moreAttrs(b)} style={wordsStyle(b, ctx.palette, ctx.theme, { ...TEXT_DEFAULT, color: b.color ? ink : (inkOnFill(b, ctx.palette) ?? ink) })}>
          {editor ?? <ColWords box={b} palette={ctx.palette} />}
        </div>
        {lw > 0 && b.line && <OutlineSvg b={b} d={d} color={paint(b.line, ctx.palette, ctx.palette.text)} lw={lw} />}
      </div>
    );
  }
  return (
    <div {...common} style={{ ...style, background: fill, border, borderRadius: b.radius }}>
      <div className={cx("sl-bx-words", (fill || border) && "is-padded")} {...moreAttrs(b)} style={wordsStyle(b, ctx.palette, ctx.theme, { ...TEXT_DEFAULT, color: ink })}>
        {editor ?? <ColWords box={b} palette={ctx.palette} />}
      </div>
    </div>
  );
}

function WidgetIn({ id, ctx }: { id: string | undefined; ctx: BoxCtx }) {
  const item = useStore((st) => (id && st.items[id]?.kind === "widget" ? (st.items[id] as WidgetItem) : null));
  if (!item) return <div className="sl-bx-gone">This widget is not on the board any more</div>;
  if (ctx.mode === "thumb" || !ctx.renderWidget) return <WidgetPlaceholder item={item} />;
  return <>{ctx.renderWidget(item)}</>;
}

function WidgetPlaceholder({ item }: { item: WidgetItem }) {
  return (
    <div className="sl-widget-card">
      <b>{item.title}</b>
      <i aria-hidden />
      <i aria-hidden />
    </div>
  );
}

/** A line or an arrow: from one corner of its box to the other, with an arrowhead or a dot at either end. */
function LineView({ box: b, ctx, style }: { box: SlideBox; ctx: BoxCtx; style: CSSProperties }) {
  const color = paint(b.line === null ? undefined : b.line, ctx.palette, ctx.palette.text);
  const lw = b.lineW ?? LINE_W;
  const g = lineEnds(b);
  const pad = Math.max(24, lw * 5);
  const len = Math.hypot(g.x2 - g.x1, g.y2 - g.y1) || 1;
  const ux = (g.x2 - g.x1) / len;
  const uy = (g.y2 - g.y1) / len;
  const head = arrowSize(lw);
  // the line stops short of an arrow's tip, so its square end never shows past the point
  const cut = (end: SlideBox["head"]) => (end === "arrow" ? head.len * 0.8 : 0);
  const a = { x: g.x1 + ux * cut(b.head), y: g.y1 + uy * cut(b.head) };
  const z = { x: g.x2 - ux * cut(b.tail), y: g.y2 - uy * cut(b.tail) };
  const tip = (x: number, y: number, dx: number, dy: number) => {
    const bx = x - dx * head.len;
    const by = y - dy * head.len;
    return `M${r2(x)},${r2(y)}L${r2(bx - dy * head.half)},${r2(by + dx * head.half)}L${r2(bx + dy * head.half)},${r2(by - dx * head.half)}Z`;
  };
  const end = (kind: SlideBox["head"], x: number, y: number, dx: number, dy: number) =>
    kind === "arrow" ? <path d={tip(x, y, dx, dy)} fill={color} /> : kind === "dot" ? <circle cx={x} cy={y} r={Math.max(5, lw * 1.6)} fill={color} /> : null;
  // an elbow (SA): its legs, square at each bend, the arrowheads on its first and last legs
  if (b.elbow) {
    const p = linePoints(b).map((q) => ({ x: q.x - b.x, y: q.y - b.y }));
    const dir = (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const l = Math.hypot(to.x - from.x, to.y - from.y) || 1;
      return { x: (to.x - from.x) / l, y: (to.y - from.y) / l };
    };
    const n = p.length;
    const d0 = dir(p[1], p[0]);
    const d1 = dir(p[n - 2], p[n - 1]);
    const legs = p.map((q, i) => (i === 0 ? { x: q.x - d0.x * cut(b.head), y: q.y - d0.y * cut(b.head) } : i === n - 1 ? { x: q.x - d1.x * cut(b.tail), y: q.y - d1.y * cut(b.tail) } : q));
    return (
      <div className="sl-bx is-line is-elbow" style={{ ...style, left: b.x - pad, top: b.y - pad, width: b.w + pad * 2, height: b.h + pad * 2, transform: undefined }} data-box={b.id} data-build={ctx.build?.(b.id)}>
        <svg className="sl-bx-svg" width={b.w + pad * 2} height={b.h + pad * 2} aria-hidden>
          <g transform={`translate(${pad} ${pad})`}>
            <polyline points={legs.map((q) => `${r2(q.x)},${r2(q.y)}`).join(" ")} fill="none" stroke={color} strokeWidth={lw} strokeLinejoin="miter" strokeLinecap={b.dash === "dot" ? "round" : "butt"} strokeDasharray={DASH[b.dash ?? "solid"](lw)} />
            {end(b.head, p[0].x, p[0].y, d0.x, d0.y)}
            {end(b.tail, p[n - 1].x, p[n - 1].y, d1.x, d1.y)}
          </g>
        </svg>
      </div>
    );
  }
  return (
    <div className="sl-bx is-line" style={{ ...style, left: b.x - pad, top: b.y - pad, width: b.w + pad * 2, height: b.h + pad * 2, transform: undefined }} data-box={b.id} data-build={ctx.build?.(b.id)}>
      <svg className="sl-bx-svg" width={b.w + pad * 2} height={b.h + pad * 2} aria-hidden>
        <g transform={`translate(${pad} ${pad})`}>
          <line x1={a.x} y1={a.y} x2={z.x} y2={z.y} stroke={color} strokeWidth={lw} strokeLinecap={lineCap(b)} strokeDasharray={dashArray(b.dash, lw, b.cap)} />
          {end(b.head, g.x1, g.y1, -ux, -uy)}
          {end(b.tail, g.x2, g.y2, ux, uy)}
        </g>
      </svg>
    </div>
  );
}

/** A line's two ends, in its box's own pixels. */
export function lineEnds(b: Pick<SlideBox, "w" | "h" | "flipH" | "flipV">) {
  return { x1: b.flipH ? b.w : 0, y1: b.flipV ? b.h : 0, x2: b.flipH ? 0 : b.w, y2: b.flipV ? 0 : b.h };
}

export const arrowSize = (lw: number) => ({ len: Math.max(14, lw * 3.4), half: Math.max(8, lw * 1.9) });

// ---------------------------------------------------------------------------
// a layout's field, placed on the slide by hand

/** A field taken out of its layout and drawn where its place says, restyled as asked; its own words inside. */
export function PlacedField({ field, place, z, children }: { field: string; place: FieldPlace; z: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const p = isPlaced(place) ? place : null;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // the field's own fit: its words shrink to fit the place, as a box's do (layout pixels, whatever the slide's scale)
    el.style.setProperty("--fit", "1");
    const inner = el.firstElementChild as HTMLElement | null;
    if (!inner || place.fit === "grow" || place.fit === "none" || field === "image" || field === "widget") return;
    const over = () => inner.offsetHeight > el.clientHeight + 1;
    if (!over()) return;
    let lo = 0.3;
    let hi = 1;
    for (let i = 0; i < 7; i++) {
      const mid = (lo + hi) / 2;
      el.style.setProperty("--fit", String(mid));
      if (over()) hi = mid;
      else lo = mid;
    }
    el.style.setProperty("--fit", String(lo));
  });
  if (!p) return null;
  return (
    <div ref={ref} className={cx("sl-placed", `is-${field}`, place.valign && `is-v-${place.valign}`)} style={{ ...boxFrame(p, place.fit === "grow"), zIndex: z }} data-placed={field} data-sa-fit={place.fit === "none" ? "none" : undefined}>
      {children}
    </div>
  );
}

/** A field's own look on this slide, over its layout's (font, size, colour, alignment, spacing), as inline styles. */
export function fieldStyle(place: FieldPlace | undefined, palette: Palette, theme: { title: string; body: string }): CSSProperties | undefined {
  if (!place) return undefined;
  const st: CSSProperties = {};
  if (place.font) st.fontFamily = fontStack(place.font, theme);
  if (place.size) {
    st.fontSize = `calc(${place.size}px * var(--fit))`;
    st.lineHeight = place.lineH ?? (place.size > 60 ? 1.1 : 1.35);
  } else if (place.lineH) st.lineHeight = place.lineH;
  if (place.weight) st.fontWeight = place.weight;
  if (place.color) st.color = paint(place.color, palette, palette.text);
  if (place.align) st.textAlign = place.align;
  // SA: letter spacing, paragraph spacing, list marks (a field shrinks with --fit)
  Object.assign(st, moreStyle(place, "var(--fit, 1)"));
  return Object.keys(st).length ? st : undefined;
}
